import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import {
  DECLINED_TRANSACTIONS_API_BASE_URL,
  ASSUME_ROLE_ENABLED,
} from "../../helpers/config";
import { AUTH } from "@/store/auth";
import { AUTH_GETTERS } from "../../store/auth/getters";
import store from "@/store";
import {
  GetDeclinedTransactionResponse,
  GetDeclinedTransactionResponseSchema,
  LatestFileUploadResponse,
  LatestFileUploadResponseSchema,
  ListDeclineCodeResponse,
  ListDeclineCodeResponseSchema,
  SearchDeclinedTransactionsResponse,
  SearchDeclinedTransactionsResponseSchema,
} from "./types";

// create axios client
const http: AxiosInstance = axios.create({
  baseURL: DECLINED_TRANSACTIONS_API_BASE_URL,
});

// get an access token before each request
http.interceptors.request.use((config: AxiosRequestConfig): any => {
  if (ASSUME_ROLE_ENABLED) {
    const assumedRole: string | undefined =
      store.getters[AUTH.NS(AUTH_GETTERS.GET_ASSUMED_ROLE)];

    if (assumedRole) {
      config.headers["Assume-Role"] = assumedRole;
    }
  }

  return store
    .dispatch(AUTH.NS(AUTH.ACTIONS.ACQUIRE_DECLINED_TRANSACTIONS_TOKEN))
    .then(
      (token) => {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (err) => {
        console.log(err);
        return err;
      }
    );
});

/**
 * Searches the historical declined transactions, to find any which match the
 * provided filters
 *
 * @param limit The max number of records to be returned
 * @param offset The number of records to skip returning
 * @param cardNumber The IFCS card number
 * @param locationCode The worldline location code
 * @param parentTerminalId The id of the parent (Master) terminal
 * @param childTerminalId The id of the child (Slave) terminal
 * @param declineCode The decline code from worldline (Note: this is a 3 letter code)
 * @param voucherTransactionNo The voucher / invoice number
 * @param totalTransactionValue The total sale value of the transaction
 * @param startDateTime The start date to query a range
 * @param endDateTime The end date to query a range
 * @returns The response body from the API
 */

export async function searchTransactions(
  limit?: number | undefined,
  offset?: number | undefined,
  cardNumber?: string | undefined,
  locationCode?: string | undefined,
  parentTerminalId?: string | undefined,
  childTerminalId?: string | undefined,
  declineCode?: string | undefined,
  voucherTransactionNo?: string | undefined,
  totalTransactionValue?: number | undefined,
  startDateTime?: Date | undefined,
  endDateTime?: Date | undefined
): Promise<SearchDeclinedTransactionsResponse> {
  const requestParams = {
    limit: limit,
    offset: offset,
    cardNumber: cardNumber,
    locationCode: locationCode,
    parentTerminalId: parentTerminalId,
    childTerminalId: childTerminalId,
    declineCode: declineCode,
    voucherTransactionNo: voucherTransactionNo,
    totalTransactionValue: totalTransactionValue,
    startDateTime: startDateTime,
    endDateTime: endDateTime,
  };

  const response = await http.get<SearchDeclinedTransactionsResponse>(
    "/v1/declined-transactions/search",
    {
      params: requestParams,
    }
  );

  return SearchDeclinedTransactionsResponseSchema.parse(response.data);
}

/**
 * Fetches the detail of a single transaction from the
 * declined transactions API.
 *
 * @param id The GUID Id of the record in cosmos
 * @returns The detail of the declined transaction
 */
export async function getTransaction(
  id: string
): Promise<GetDeclinedTransactionResponse> {
  const response = await http.get<GetDeclinedTransactionResponse>(
    `/v1/declined-transactions/${id}`
  );

  return GetDeclinedTransactionResponseSchema.parse(response.data);
}

/**
 * Gets the latest list of decline codes from the API
 * @returns The response body from the API
 */

export async function getDeclineCodes(): Promise<ListDeclineCodeResponse> {
  const response = await http.get<ListDeclineCodeResponse>(
    "/v1/decline-codes/",
    {}
  );

  return ListDeclineCodeResponseSchema.parse(response.data);
}

/**
 * Gets metadata about the latest file upload from the api
 * @returns The response body from the API
 */

export async function getLatestFileUpload(): Promise<LatestFileUploadResponse> {
  const response = await http.get<LatestFileUploadResponse>(
    "/v1/file-uploads/latest",
    {}
  );

  return LatestFileUploadResponseSchema.parse(response.data);
}

export default {
  searchTransactions,
  getDeclineCodes,
  getLatestFileUpload,
};
